import React, { useState, useRef, useEffect, useContext, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import "../../../assets/css/doctor/docnavbar/DoctorHeader.css";
import { FaBell, FaEnvelope, FaUserCircle } from "react-icons/fa";
import {
  FiCalendar,
  FiChevronsLeft,
  FiChevronsRight,
  FiHelpCircle,
  FiLogOut,
  FiSearch,
  FiSettings,
  FiUser,
  FiX,
} from "react-icons/fi";
import { AppContext } from "../../../context/AppContext";

// Route -> page title shown in the header. Falls back to "Therapists".
const PAGE_TITLES = [
  ["/doctor/kyc", "KYC Verification"],
  ["/doctor/appointments", "Appointments"],
  ["/doctor/schedulemanagement", "Schedule"],
  ["/doctor/wallet", "Wallet"],
  ["/doctor/history", "History"],
  ["/doctor/prescriptions", "Prescriptions"],
  ["/doctor/dispute", "Disputes"],
  ["/doctor/settings", "Settings"],
  ["/doctor/profile", "My Profile"],
  ["/doctor/notifications", "Notifications"],
  ["/doctor/doctormessages", "Messages"],
  ["/doctor/helpsupport", "Help & Support"],
  ["/doctor/patients", "Patient Details"],
];

const getPageTitle = (pathname) => {
  const p = (pathname || "").toLowerCase().replace(/\/+$/, "");
  if (p === "" || p === "/doctor") return "Dashboard";
  const match = PAGE_TITLES.find(([prefix]) => p === prefix || p.startsWith(prefix + "/"));
  return match ? match[1] : "Therapists";
};

const getInitial = (name) => (name || "?").trim().charAt(0).toUpperCase();

const DoctorHeader = ({ toggleSidebar, isSidebarOpen }) => {
  const { baseUrl, logout } = useContext(AppContext);
  const navigate = useNavigate();
  const location = useLocation();

  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [profile, setProfile] = useState(null);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);

  // ── Search ──────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const searchDebounceRef = useRef(null);
  const searchBoxRef = useRef(null);
  const searchInputRef = useRef(null);

  const profileRef = useRef(null);

  const token = localStorage.getItem("therapist_token");
  const authHeaders = {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    },
  };

  const pageTitle = getPageTitle(location.pathname);
  const avatarUrl = profile?.profile_image_url || profile?.profile_image || "";

  // Close profile dropdown / search results on outside click, Esc closes both
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
      if (searchBoxRef.current && !searchBoxRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setShowProfileMenu(false);
        setShowSearchResults(false);
        setMobileSearchOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  // Close menus whenever the route changes
  useEffect(() => {
    setShowProfileMenu(false);
    setShowSearchResults(false);
    setMobileSearchOpen(false);
  }, [location.pathname]);

  // Focus the input when the mobile search bar opens
  useEffect(() => {
    if (mobileSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [mobileSearchOpen]);

  // ── Profile summary (name/email/photo for dropdown) ───────────
  const fetchProfile = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${baseUrl}/doctor/Header/profile`, {
        headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
      });
      setProfile(res.data?.data ?? null);
    } catch (err) {
      console.log("Header profile fetch error:", err);
      if (err?.response?.status === 401) logout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUrl, token]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // ── Unread notifications + messages counts ─────────────────────
  // Re-fetched on every route change (so badges update right after the
  // doctor reads something) and polled once a minute.
  useEffect(() => {
    if (!token) return;

    const fetchCounts = async () => {
      try {
        const [notifRes, msgRes] = await Promise.all([
          axios.get(`${baseUrl}/doctor/Header/notifications/unread-count`, authHeaders),
          axios.get(`${baseUrl}/doctor/Header/messages/unread-count`, authHeaders),
        ]);
        setUnreadNotifications(notifRes.data?.data?.unread_count ?? 0);
        setUnreadMessages(msgRes.data?.data?.unread_count ?? 0);
      } catch (err) {
        console.log("Header counts fetch error:", err);
        // Non-critical — badges just keep their last value on failure
      }
    };

    fetchCounts();
    const interval = setInterval(fetchCounts, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUrl, token, location.pathname]);

  // ── Live search (debounced) ─────────────────────────────────
  useEffect(() => {
    if (!token) return;

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    if (searchQuery.trim() === "") {
      setSearchResults(null);
      setShowSearchResults(false);
      return;
    }

    searchDebounceRef.current = setTimeout(async () => {
      setSearchLoading(true);
      setShowSearchResults(true);
      try {
        const res = await axios.get(`${baseUrl}/doctor/Header/search`, {
          ...authHeaders,
          params: { query: searchQuery.trim() },
        });
        setSearchResults(res.data?.data ?? { patients: [], appointments: [] });
      } catch (err) {
        console.log("Header search error:", err);
        if (err?.response?.status === 401) logout();
      } finally {
        setSearchLoading(false);
      }
    }, 400);

    return () => clearTimeout(searchDebounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, baseUrl, token]);

  const toggleProfileMenu = () => {
    // Refresh name/email/photo each time the menu opens (picks up profile edits)
    if (!showProfileMenu) fetchProfile();
    setShowProfileMenu(!showProfileMenu);
  };

  const toggleMobileSearch = () => {
    if (mobileSearchOpen) {
      setShowSearchResults(false);
    }
    setMobileSearchOpen(!mobileSearchOpen);
  };

  const handleLogout = async () => {
    setShowProfileMenu(false);
    try {
      await axios.post(`${baseUrl}/doctor/Header/logout`, {}, authHeaders);
    } catch (err) {
      console.log("Logout API error:", err);
      // Even if the backend call fails, still clear the local session —
      // we don't want the user stuck logged in on the frontend.
    } finally {
      logout();
      navigate("/doctor/login");
    }
  };

  const goTo = (path) => {
    navigate(path);
    setShowProfileMenu(false);
  };

  const closeSearch = () => {
    setShowSearchResults(false);
    setSearchQuery("");
    setMobileSearchOpen(false);
  };

  const handleSelectPatient = (patientId) => {
    closeSearch();
    navigate(`/doctor/patients/${patientId}`);
  };

  const handleSelectAppointment = (appointmentId) => {
    closeSearch();
    navigate(`/doctordashboard/appointments/${appointmentId}`);
  };

  const noResults =
    searchResults &&
    !(searchResults.patients?.length > 0) &&
    !(searchResults.appointments?.length > 0);

  return (
    <div className="doctor-header">
      {/* Toggle Button */}
      <button
        className="doctor-header-toggle-btn"
        onClick={toggleSidebar}
        title={isSidebarOpen ? "Close Sidebar" : "Open Sidebar"}
        aria-label={isSidebarOpen ? "Close sidebar" : "Open sidebar"}
      >
        {isSidebarOpen ? <FiChevronsLeft /> : <FiChevronsRight />}
      </button>

      <h2 className="doctor-header-title">{pageTitle}</h2>

      <div className="doctor-header-actions">
        {/* Search (inline on desktop, full-width bar under the header on mobile) */}
        <div
          className={`doctor-header-search${mobileSearchOpen ? " is-open" : ""}`}
          ref={searchBoxRef}
        >
          <div className="doctor-header-search-field">
            <FiSearch className="doctor-header-search-icon" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search patients, appointments..."
              className="doctor-header-search-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => searchResults && setShowSearchResults(true)}
            />
            {searchQuery && (
              <button
                type="button"
                className="doctor-header-search-clear"
                aria-label="Clear search"
                onClick={() => {
                  setSearchQuery("");
                  if (searchInputRef.current) searchInputRef.current.focus();
                }}
              >
                <FiX />
              </button>
            )}
          </div>

          {showSearchResults && (
            <div className="doctor-header-search-results">
              {searchLoading && (
                <div className="doctor-header-search-loading">Searching…</div>
              )}

              {!searchLoading && noResults && (
                <div className="doctor-header-search-empty">No results found.</div>
              )}

              {!searchLoading && searchResults?.patients?.length > 0 && (
                <div className="doctor-header-search-group">
                  <span className="doctor-header-search-group-title">Patients</span>
                  {searchResults.patients.map((p) => (
                    <div
                      key={p.id}
                      className="doctor-header-search-item"
                      onClick={() => handleSelectPatient(p.id)}
                    >
                      <div className="doctor-header-search-avatar">{getInitial(p.full_name)}</div>
                      <div className="doctor-header-search-text">
                        <div className="doctor-header-search-name">{p.full_name}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {!searchLoading && searchResults?.appointments?.length > 0 && (
                <div className="doctor-header-search-group">
                  <span className="doctor-header-search-group-title">Appointments</span>
                  {searchResults.appointments.map((a) => (
                    <div
                      key={a.id}
                      className="doctor-header-search-item"
                      onClick={() => handleSelectAppointment(a.id)}
                    >
                      <div className="doctor-header-search-avatar doctor-header-search-avatar-appt">
                        <FiCalendar />
                      </div>
                      <div className="doctor-header-search-text">
                        <div className="doctor-header-search-name">{a.patient_name}</div>
                        <div className="doctor-header-search-meta">{a.session_date}</div>
                      </div>
                      {a.status && (
                        <span
                          className={`doctor-header-status doctor-header-status-${String(a.status).toLowerCase()}`}
                        >
                          {a.status}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Mobile-only search icon */}
        <button
          type="button"
          aria-label="Search"
          className="doctor-header-icon-btn doctor-header-search-toggle"
          onClick={toggleMobileSearch}
        >
          {mobileSearchOpen ? <FiX /> : <FiSearch />}
        </button>

        <button
          aria-label="Notifications"
          className="doctor-header-icon-btn"
          onClick={() => navigate("/doctor/notifications")}
        >
          <FaBell />
          {unreadNotifications > 0 && (
            <span className="doctor-header-badge">
              {unreadNotifications > 99 ? "99+" : unreadNotifications}
            </span>
          )}
        </button>

        <button
          aria-label="Messages"
          className="doctor-header-icon-btn"
          onClick={() => navigate("/doctor/doctormessages")}
        >
          <FaEnvelope />
          {unreadMessages > 0 && (
            <span className="doctor-header-badge">
              {unreadMessages > 99 ? "99+" : unreadMessages}
            </span>
          )}
        </button>

        <div ref={profileRef} className="doctor-header-profile">
          <button
            type="button"
            aria-label="Profile menu"
            onClick={toggleProfileMenu}
            className="doctor-header-profile-icon"
          >
            {avatarUrl ? (
              <img src={avatarUrl} alt="Profile" className="doctor-header-profile-img" />
            ) : (
              <FaUserCircle />
            )}
          </button>

          {showProfileMenu && (
            <div className="doctor-profile-dropdown">
              <div className="doctor-profile-dropdown-header">
                <div className="doctor-profile-avatar">
                  {avatarUrl ? <img src={avatarUrl} alt="Profile" /> : <FaUserCircle />}
                </div>
                <div className="doctor-profile-info">
                  <h4>{profile?.full_name || "Doctor"}</h4>
                  <p>{profile?.email || ""}</p>
                </div>
              </div>

              <div className="doctor-profile-dropdown-divider"></div>

              <ul className="doctor-profile-dropdown-menu">
                <li className="doctor-profile-dropdown-item" onClick={() => goTo("/doctor/profile")}>
                  <FiUser /> <span>My Profile</span>
                </li>
                <li className="doctor-profile-dropdown-item" onClick={() => goTo("/doctor/settings")}>
                  <FiSettings /> <span>Settings</span>
                </li>
                <li className="doctor-profile-dropdown-item" onClick={() => goTo("/doctor/helpsupport")}>
                  <FiHelpCircle /> <span>Help & Support</span>
                </li>
              </ul>

              <div className="doctor-profile-dropdown-divider"></div>

              <div className="doctor-profile-dropdown-footer">
                <button onClick={handleLogout} className="doctor-logout-btn">
                  <FiLogOut /> Logout
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DoctorHeader;